import { resources } from "@constants/hardcoded-strings";
import { ValidationErrorsContainer } from "@containers/common/validation-errors-container/validation-errors-container";
import { RelyingPartyRole } from "@idw-types/common";
import { OrganizationUserDetailsAdminApiAccessValidationErrors } from "@idw-types/common/validation/organization/organization-user-details/organization-user-details-admin-api-access-validation-errors";
import { AxnAdminUserPropertyBundle } from "@idw-types/data/axn-admin-user-property-bundle";
import { AxnAdminUserProperty } from "@idw-types/data/axn-admin-user/axn-admin-user-property";
import {
    AxnAdminUser,
    AxnAdminUserDetails,
    OrganizationUserDetailApiAccessValidators,
    OrganizationUserDetailErrors
} from "@idw-types/data/axn-admin-user";
import { hasProperties } from "@utils/object-util";
import {
    getErrorAlert,
    getTabTitle
} from "@utils/tab-util";

export const organizationUserDetailsApiAccessValidations: Array<OrganizationUserDetailApiAccessValidators> = [
    {
        name: 'atLeastOneLowercaseLetter',
        label: 'A lower case letter',
        isValid: (value: string) => /[a-z]+/.test(value || '')
    },
    {
        name: 'atLeastOneCapitalLetter',
        label: 'A capital (uppercase) letter',
        isValid: (value: string) => /[A-Z]+/.test(value)
    },
    {
        name: 'atLeastOneNumber',
        label: 'A number',
        isValid: (value: string) => /\d+/.test(value)
    },
    {
        name: 'minimumLength',
        label: 'Minimum 8 characters',
        isValid: (value: string) => value?.length >= 8
    }
];


export const getOrganizationUserDetailsValidationErrors = (
    axnAdminUser: AxnAdminUser,
    adminProperty: AxnAdminUserProperty,
    organizationRole: AxnAdminUserPropertyBundle,
    isCustomOrgRole: boolean,
    relyingPartyRoles: Array<RelyingPartyRole>,
    initialApiUserName: string,
    isApiAccessEnabled: boolean,
    isApiPasswordSet: boolean
) => {
    let count = 0;
    const { axnAdminUserDetails } = axnAdminUser;
    const organizationUserDetailsMainValidationErrors = getOrganizationUserDetailsMainValidationErrors(
        axnAdminUser,
        adminProperty,
        organizationRole,
        isCustomOrgRole,
        relyingPartyRoles
    );

    const organizationUserDetailApiAccessValidationErrors = getOrganizationUserDetailApiAccessValidationErrors(
        axnAdminUserDetails,
        initialApiUserName,
        isApiAccessEnabled,
        isApiPasswordSet
    );

    count = organizationUserDetailsMainValidationErrors.count + organizationUserDetailApiAccessValidationErrors.count;

    return {
        details: organizationUserDetailsMainValidationErrors,
        adminApiAccess: organizationUserDetailApiAccessValidationErrors,
        count
    }
}

export const getOrganizationUserDetailsMainValidationErrors = (
    axnAdminUser: AxnAdminUser,
    adminProperty: AxnAdminUserProperty,
    organizationRole: AxnAdminUserPropertyBundle,
    isCustomOrgRole: boolean,
    relyingPartyRoles: Array<RelyingPartyRole>
) => {
    let firstNameError = null;
    let lastNameError = null;
    let emailError = null;
    let identityProviderError = null;
    let relyingPartyError = null;
    let count = 0;

    const {
        firstName,
        lastName,
        email,
        identityProviderID
    } = axnAdminUser.axnAdminUserDetails;

    if (firstName === "") {
        count++;
        firstNameError = getErrorAlert(resources.common.requiredField);

    }

    if (lastName === "") {
        count++;
        lastNameError = getErrorAlert(resources.common.requiredField);
    }

    if (email === "") {
        count++;
        emailError = getErrorAlert(resources.common.requiredField);
    }

    if (identityProviderID === null) {
        count++;
        identityProviderError = getErrorAlert(resources.common.requiredField);
    }

    if (
        !organizationRole &&
        !isCustomOrgRole &&
        !adminProperty &&
        relyingPartyRoles.length === 0
    ) {
        const rpIdsInState = axnAdminUser.axnAdminUserRPProperties
            .map(property => property.relyingPartyID)
            .filter((rpId, index) =>
                axnAdminUser.axnAdminUserRPProperties.findIndex(property => property.relyingPartyID === rpId) === index
            );

        if (rpIdsInState.length === 0) {
            count++;
            relyingPartyError = getErrorAlert(resources.modals.addOrganizationUser.noRelyingPartyRolesError);
        }
    }

    return {
        count,
        firstNameError,
        lastNameError,
        emailError,
        identityProviderError,
        relyingPartyError,
        tabTitle: getTabTitle(resources.pages.organizationUserDetails.tabs.details.tabName, count)
    }
}

export const getOrganizationUserDetailApiAccessValidationErrors = (currentAxnAdminUserDetails: AxnAdminUserDetails, initialApiUserName: string, isApiAccessEnabled: boolean, isApiPasswordSet: boolean) => {
    const { apiUserName, apiPassword } = currentAxnAdminUserDetails;

    const organizationUserDetailErrorNodes: OrganizationUserDetailsAdminApiAccessValidationErrors = {
        count: 0,
        apiUserNameError: null,
        apiPasswordError: null,
        tabTitle: getTabTitle(resources.pages.organizationUserDetails.tabs.adminApiAccess.tabName, 0)
    };

    const shouldIgnoreApiUserNameValidation = apiUserName === initialApiUserName || !isApiAccessEnabled;
    const shouldIgnoreApiPasswordValidation = !isApiAccessEnabled || isApiPasswordSet;

    if (!shouldIgnoreApiUserNameValidation || !shouldIgnoreApiPasswordValidation) {
        const apiAccessValidations = generateApiAccessValidations(apiUserName, apiPassword, shouldIgnoreApiUserNameValidation, shouldIgnoreApiPasswordValidation);
        return generateValidationErrorNodes(apiAccessValidations);
    }

    return organizationUserDetailErrorNodes;
}

export const generateApiAccessValidations = (apiUserName: string, apiPassword: string, shouldIgnoreApiUserNameValidation: boolean, shouldIgnoreApiPasswordValidation: boolean) => {
    const organizationUserDetailErrors: OrganizationUserDetailErrors = {
        count: 0,
        apiUserName: {},
        apiPassword: {}
    };

    let count = 0;
    organizationUserDetailsApiAccessValidations.forEach(validator => {
        const { name, isValid, label } = validator;

        if (!shouldIgnoreApiUserNameValidation) {
            const isApiUserNameValid = isValid(apiUserName);
            organizationUserDetailErrors.apiUserName[name] = {
                valid: isApiUserNameValid,
                errorMessage: label,
            }

            count = !isApiUserNameValid ? count + 1 : count;
        }

        if (!shouldIgnoreApiPasswordValidation) {
            const isApiPasswordValid = isValid(apiPassword);
            organizationUserDetailErrors.apiPassword[name] = {
                valid: isApiPasswordValid,
                errorMessage: label
            }

            count = !isApiPasswordValid ? count + 1 : count;
        }
    });

    organizationUserDetailErrors.count = count;

    return organizationUserDetailErrors;
}

const generateValidationErrorNodes = (organizationUserDetailErrors: OrganizationUserDetailErrors): OrganizationUserDetailsAdminApiAccessValidationErrors => {
    let apiUserNameError = null;
    let apiPasswordError = null;

    const { apiUserName, apiPassword } = organizationUserDetailErrors;

    if (hasProperties(apiUserName)) {
        apiUserNameError = <ValidationErrorsContainer organizationUserDetailErrors={organizationUserDetailErrors.apiUserName} label={'Username'} />
    }

    if (hasProperties(apiPassword)) {
        apiPasswordError = <ValidationErrorsContainer organizationUserDetailErrors={organizationUserDetailErrors.apiPassword} label={'Password'} />
    }

    return {
        count: organizationUserDetailErrors.count,
        apiUserNameError,
        apiPasswordError,
        tabTitle: getTabTitle(resources.pages.organizationUserDetails.tabs.adminApiAccess.tabName, organizationUserDetailErrors.count)
    };
}

